import type { MdmAddressLine } from "@/types/cat/mdm-account.types";

export const MDM_ADDRESS_LINE_USE = {
  floorApartment: "line2",
  buildingResidence: "line3",
  street: "line4",
  postBox: "line5",
} as const;

export type ParsedMdmAddress = {
  addressLine: string;
  addressFloorApartment: string;
  addressBuildingResidence: string;
  postBox: string;
};

export const EMPTY_PARSED_MDM_ADDRESS: ParsedMdmAddress = {
  addressLine: "",
  addressFloorApartment: "",
  addressBuildingResidence: "",
  postBox: "",
};

function lineValue(line: MdmAddressLine | undefined): string {
  return line?.value?.trim() ?? "";
}

function findLineByUse(
  lines: MdmAddressLine[],
  use: string,
): MdmAddressLine | undefined {
  return lines.find((l) => l.use?.trim().toLowerCase() === use.toLowerCase());
}

export function parseMdmAddressLines(
  lines: MdmAddressLine[] | undefined,
): ParsedMdmAddress {
  if (!lines?.length) {
    return { ...EMPTY_PARSED_MDM_ADDRESS };
  }

  const hasKnownUse = lines.some((l) => {
    const use = l.use?.trim().toLowerCase();
    return (
      use === MDM_ADDRESS_LINE_USE.floorApartment ||
      use === MDM_ADDRESS_LINE_USE.buildingResidence ||
      use === MDM_ADDRESS_LINE_USE.street ||
      use === MDM_ADDRESS_LINE_USE.postBox
    );
  });

  if (!hasKnownUse) {
    const joined = lines
      .map((l) => lineValue(l))
      .filter(Boolean)
      .join(" ")
      .trim();
    return {
      ...EMPTY_PARSED_MDM_ADDRESS,
      addressLine: joined,
    };
  }

  return {
    addressLine: lineValue(findLineByUse(lines, MDM_ADDRESS_LINE_USE.street)),
    addressFloorApartment: lineValue(
      findLineByUse(lines, MDM_ADDRESS_LINE_USE.floorApartment),
    ),
    addressBuildingResidence: lineValue(
      findLineByUse(lines, MDM_ADDRESS_LINE_USE.buildingResidence),
    ),
    postBox: lineValue(findLineByUse(lines, MDM_ADDRESS_LINE_USE.postBox)),
  };
}

export function buildMdmAddressLines(
  parsed: ParsedMdmAddress,
): MdmAddressLine[] {
  const entries: Array<{ use: string; value: string }> = [
    {
      use: MDM_ADDRESS_LINE_USE.floorApartment,
      value: parsed.addressFloorApartment.trim(),
    },
    {
      use: MDM_ADDRESS_LINE_USE.buildingResidence,
      value: parsed.addressBuildingResidence.trim(),
    },
    { use: MDM_ADDRESS_LINE_USE.street, value: parsed.addressLine.trim() },
    { use: MDM_ADDRESS_LINE_USE.postBox, value: parsed.postBox.trim() },
  ];

  return entries
    .filter((e) => e.value.length > 0)
    .map((e) => ({ use: e.use, value: e.value }));
}

export function formatAddressLinesForDisplay(
  parsed: ParsedMdmAddress,
): string[] {
  return [
    parsed.addressLine,
    parsed.addressFloorApartment,
    parsed.addressBuildingResidence,
    parsed.postBox,
  ].filter((v) => v.trim().length > 0);
}
